import { PersonArrowIcon, PersonXIcon } from '@moia-dev/pace-icons'
import { Button, LoadingIndicator } from '@moia-dev/pace-web'
import { isPast } from 'date-fns'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { PassengerCard } from '../PassengerCard'
import { AuthenticationStatus } from '../PassengerCard/PassengerCard'
import type {
  AdVehicleStreamPassengerCardCustomerInfoFragment,
  AdVehicleStreamPassengerCardSeatInfoFragment,
  AdVehicleStreamPassengerCardStopInfoFragment,
} from '../PassengerCard/PassengerCard.hook'
import { CheckInPassengerDialog } from './CheckInPassengerDialog'
import { ConfirmManualNoShowDialog } from './ConfirmManualNoShowDialog'

type BoardingPassengerWithAuthenticationProps = {
  tripId: string
  vehicleId: string
  customer: AdVehicleStreamPassengerCardCustomerInfoFragment | null
  waypointStop: AdVehicleStreamPassengerCardStopInfoFragment | null
  seats: AdVehicleStreamPassengerCardSeatInfoFragment
  eta: Date
  authenticationStatus: AuthenticationStatus
  isBoarded: boolean
  isNoShow: boolean
}
export const BoardingPassengerCard = ({
  tripId,
  vehicleId,
  customer,
  waypointStop,
  seats,
  eta,
  authenticationStatus,
  isNoShow,
  isBoarded,
}: BoardingPassengerWithAuthenticationProps) => {
  const { t } = useTranslation()

  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [isNoShowDialogOpen, setIsNoShowDialogOpen] = useState(false)

  const isCheckInOverdue =
    authenticationStatus === AuthenticationStatus.IN_PROGRESS && isPast(eta)
  const isCheckInSuccessful =
    authenticationStatus === AuthenticationStatus.CONFIRMED
  const isManualNoShowRequested =
    authenticationStatus === AuthenticationStatus.MANUAL_NO_SHOW_REQUESTED &&
    isPast(eta)
  const isManualNoShowPending =
    authenticationStatus === AuthenticationStatus.MANUAL_NO_SHOW_PENDING
  const isManualNoShowTriggered =
    authenticationStatus === AuthenticationStatus.NO_SHOW_TRIGGERED
  const isTripCancelled =
    authenticationStatus === AuthenticationStatus.CANCELLED
  const isManualCheckInPending =
    authenticationStatus ===
    AuthenticationStatus.MANUAL_PASSENGER_CHECK_IN_PENDING

  const isCardDisabled =
    isManualNoShowTriggered ||
    isManualNoShowPending ||
    isManualCheckInPending ||
    isTripCancelled

  const cardHighlight = isCheckInSuccessful
    ? 'success'
    : isCheckInOverdue || isManualNoShowRequested
      ? 'accent'
      : 'disabled'

  const getActionButton = () => {
    if (isManualNoShowRequested || isManualNoShowPending) {
      return (
        <div
          css={theme => ({
            display: 'flex',
            gap: theme.space.Space2,
            flexDirection: 'row',
            flexWrap: 'wrap',
            justifyContent: 'flex-end',
            '@media (max-width: 400px)': {
              flexDirection: 'column',
            },
          })}
        >
          <Button
            label={t('map_confirmNoShow_button')}
            variant={'primary'}
            icon={
              isManualNoShowPending || isManualCheckInPending ? (
                <LoadingIndicator />
              ) : (
                <PersonXIcon />
              )
            }
            disabled={isCardDisabled}
            onClick={() => setIsNoShowDialogOpen(true)}
          />
          <Button
            variant={'secondary'}
            label={t('adFleetMap_cscRemoteCheckin_button')}
            icon={
              isManualNoShowPending || isManualCheckInPending ? (
                <LoadingIndicator />
              ) : (
                <PersonArrowIcon />
              )
            }
            disabled={isCardDisabled}
            onClick={() => setIsDialogOpen(true)}
          />
        </div>
      )
    }
    if (!isCheckInSuccessful && !isManualNoShowTriggered && !isTripCancelled) {
      return (
        <Button
          variant={'secondary'}
          label={t('adFleetMap_cscRemoteCheckin_button')}
          icon={
            isManualNoShowPending || isManualCheckInPending ? (
              <LoadingIndicator />
            ) : (
              <PersonArrowIcon />
            )
          }
          disabled={isCardDisabled}
          onClick={() => setIsDialogOpen(true)}
        />
      )
    }

    return null
  }

  return (
    <>
      <PassengerCard
        customer={customer}
        waypointStop={waypointStop}
        disabled={isCardDisabled}
        eta={eta}
        seats={seats}
        variant={cardHighlight}
        actionButton={getActionButton()}
        isNoShow={isNoShow}
        isBoarded={isBoarded}
        authenticationStatus={authenticationStatus}
      />

      {isDialogOpen && (
        <CheckInPassengerDialog
          isDialogOpen={isDialogOpen}
          tripId={tripId}
          vehicleId={vehicleId}
          setIsDialogOpen={setIsDialogOpen}
        />
      )}

      {isNoShowDialogOpen && (
        <ConfirmManualNoShowDialog
          tripId={tripId}
          vehicleId={vehicleId}
          isDialogOpen={isNoShowDialogOpen}
          setIsDialogOpen={setIsNoShowDialogOpen}
        />
      )}
    </>
  )
}
